import { useRef, useState } from "react";
import { useQuery } from "@connectrpc/connect-query";
import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { formatNumber } from "@yatayat/core/money";
import { ComplaintCategory, ComplaintService, ComplaintStatus, type Complaint } from "@yatayat/gen/yatayat/complaints/v1/complaints_pb";
import { ConsoleService } from "@yatayat/gen/yatayat/console/v1/console_pb";

import { ComplaintDetail } from "~/components/console/ComplaintDetail";
import { ComplaintFilters, complaintCategoryNames, complaintStatusNames, type ComplaintFilterValues } from "~/components/console/ComplaintFilters";
import { ComplaintList } from "~/components/console/ComplaintList";
import { Pager, usePageTokens } from "~/components/console/ComplianceKit";
import { ComplaintsMap } from "~/components/maps/ComplaintsMap";
import { useStaffSessionExpiry } from "~/components/OperatorTrip";
import { QueryError } from "~/components/QueryState";
import { useI18n } from "~/lib/i18n";
import { retryTransient } from "~/lib/rpc/errors";
import { searchString } from "~/lib/search";
import { useStaffSession } from "~/lib/staff";
import { ui } from "~/lib/ui";

type ComplaintsSearch = Partial<ComplaintFilterValues>;

// Filters live in the URL so the compliance evidence can link to one operator's complaints.
export const Route = createFileRoute("/console/complaints")({
  validateSearch: (search: Record<string, unknown>): ComplaintsSearch => ({
    category: searchString(search.category),
    status: searchString(search.status),
    corridor: searchString(search.corridor),
    operator: searchString(search.operator),
  }),
  component: ConsoleComplaints,
});

const pageSize = 25;
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const routeCode = /^[A-Z0-9-]{3,32}$/;

function ConsoleComplaints() {
  const { t, lang } = useI18n();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const staffId = useStaffSession().session?.staffId ?? "";

  // Values the api would reject are dropped rather than sent.
  const values: ComplaintFilterValues = {
    category: search.category && Object.hasOwn(complaintCategoryNames, search.category) ? search.category : "",
    status: search.status && Object.hasOwn(complaintStatusNames, search.status) ? search.status : "",
    corridor: search.corridor && routeCode.test(search.corridor) ? search.corridor : "",
    operator: search.operator && uuid.test(search.operator) ? search.operator : "",
  };
  const pages = usePageTokens(JSON.stringify(values));

  // Newest first, so a complaint filed during the demo tops the first page.
  const list = useQuery(
    ComplaintService.method.listComplaints,
    {
      category: values.category ? complaintCategoryNames[values.category] : ComplaintCategory.UNSPECIFIED,
      status: values.status ? complaintStatusNames[values.status] : ComplaintStatus.UNSPECIFIED,
      routeCode: values.corridor,
      operatorId: values.operator,
      pageSize,
      pageToken: pages.token,
    },
    { retry: retryTransient, placeholderData: keepPreviousData },
  );
  const corridors = useQuery(ConsoleService.method.listCorridors, {}, { retry: retryTransient, staleTime: 5 * 60_000 });
  const unauthenticated = useStaffSessionExpiry(list.error);

  // The chosen complaint as last seen, so it stays open across pages and filters, and
  // shows the server's answer to an action until the list catches up with it.
  const [chosen, setChosen] = useState<{ complaint: Complaint; at: number }>();
  const [notice, setNotice] = useState("");
  const headingRef = useRef<HTMLHeadingElement>(null);

  const complaints = list.data?.complaints ?? [];
  const selectedId = chosen?.complaint.id;
  const fromList = complaints.find((c) => c.id === selectedId);
  const selected = fromList && list.dataUpdatedAt >= (chosen?.at ?? 0) ? fromList : chosen?.complaint;

  function select(complaintId: string) {
    const complaint = complaints.find((c) => c.id === complaintId);
    if (!complaint) return;
    setChosen({ complaint, at: list.dataUpdatedAt });
    setNotice("");
    // After the panel renders, so a keyboard user lands on the complaint they chose.
    requestAnimationFrame(() => headingRef.current?.focus());
  }

  function changeFilters(next: Partial<ComplaintFilterValues>) {
    setNotice("");
    void navigate({
      search: (prev) => {
        const merged = { ...prev, ...next };
        return {
          category: merged.category || undefined,
          status: merged.status || undefined,
          corridor: merged.corridor || undefined,
          operator: merged.operator || undefined,
        };
      },
      replace: true,
      resetScroll: false,
    });
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className={ui.h1}>{t("complaintsTitle")}</h1>
        <p className={ui.muted}>{t("consoleComplaintsIntro")}</p>
      </div>

      <ComplaintFilters values={values} onChange={changeFilters} />

      <div className="grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <section aria-label={t("consoleComplaintList")} className="min-w-0 space-y-3">
          <p aria-live="polite" className={ui.muted}>
            {list.data &&
              (complaints.length === 0
                ? ""
                : t("consoleComplaintsShown", { n: formatNumber(complaints.length, lang), page: formatNumber(pages.page, lang) }))}
          </p>
          {list.isPending && <p className={ui.muted}>{t("loading")}</p>}
          {list.isError && !list.data && !unauthenticated && <QueryError onRetry={() => void list.refetch()} busy={list.isFetching} />}
          {list.data && complaints.length === 0 && <p className={ui.empty}>{t("consoleComplaintNone")}</p>}
          {complaints.length > 0 && (
            <div aria-busy={list.isFetching} className={list.isPlaceholderData ? "opacity-60" : undefined}>
              <ComplaintList complaints={complaints} selectedId={selectedId} onSelect={select} />
            </div>
          )}
          <Pager
            page={pages.page}
            nextToken={list.data?.nextPageToken ?? ""}
            busy={list.isFetching}
            onPrevious={pages.previous}
            onNext={pages.next}
          />
        </section>

        <div className="min-w-0 space-y-4 xl:sticky xl:top-4 xl:self-start">
          <ComplaintsMap
            complaints={complaints}
            corridors={corridors.data?.corridors}
            selectedId={selectedId}
            onSelect={select}
            className="h-72 w-full"
          />
          <div role="status">
            {notice && (
              <p className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                <span aria-hidden="true">✓ </span>
                {notice}
              </p>
            )}
          </div>
          {selected ? (
            <ComplaintDetail
              key={selected.id}
              complaint={selected}
              staffId={staffId}
              headingRef={headingRef}
              onUpdated={(complaint, message) => {
                setChosen({ complaint, at: Date.now() });
                setNotice(message);
                // The button just used goes away once the complaint moves on.
                requestAnimationFrame(() => headingRef.current?.focus());
              }}
            />
          ) : (
            complaints.length > 0 && <p className={ui.empty}>{t("consoleComplaintChoose")}</p>
          )}
        </div>
      </div>
    </div>
  );
}
